import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectContentWithSearch,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ArrowLeft, Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import {
  jobWorkApiService,
  JobWorkJobType,
  JobWorkOrderDto,
  JobWorkOrderIssueLineDto,
  JobWorkOrderOutputLineDto,
  JobWorkOrderWorkOrderDto,
  JobWorkPartyType,
  JobWorkProcessLookupDto,
  JobWorkWipWorkOrderDto,
  JOB_WORK_JOB_TYPES,
  JOB_WORK_PARTY_TYPES,
  jobWorkUsesRawItem,
} from '../services/jobWorkApi';
import { supplierApiService, Supplier } from '../services/supplierApi';
import { endgoodApiService, Endgood, EndgoodIdNameCategoryDto } from '../services/endgoodApi';
import { rawItemApiService, RawItem } from '../services/rawItemApi';
import { branchApiService, Branch } from '../services/branchApi';
import { Checkbox } from './ui/checkbox';

type IssueLineForm = {
  key: string;
  jobType: JobWorkJobType;
  itemId: string;
  plannedQty: string;
  uom: string;
  notes: string;
};

type OutputLineForm = {
  key: string;
  jobType: JobWorkJobType;
  /** End process (till which process work was done). */
  processId: string;
  plannedQty: string;
  uom: string;
  notes: string;
};

function newKey() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function emptyIssueLine(): IssueLineForm {
  return {
    key: newKey(),
    jobType: 'WIP',
    itemId: '',
    plannedQty: '',
    uom: 'NOS',
    notes: '',
  };
}

function emptyOutputLine(): OutputLineForm {
  return {
    key: newKey(),
    jobType: 'FINISHED_GOOD',
    processId: '',
    plannedQty: '',
    uom: 'NOS',
    notes: '',
  };
}

function todayInputValue() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function JobWorkOrderFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const { selectedBranch } = useBranch();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [partyType, setPartyType] = useState<JobWorkPartyType>('BRANCH');
  const [supplierId, setSupplierId] = useState('');
  const [toBranchId, setToBranchId] = useState('');
  const [endgoodId, setEndgoodId] = useState('');
  const [processId, setProcessId] = useState('');
  /** Selected WO id → qty string (defaults to WIP qty on select). */
  const [selectedWorkOrders, setSelectedWorkOrders] = useState<Record<number, string>>({});
  const [orderDate, setOrderDate] = useState(todayInputValue());
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [notes, setNotes] = useState('');
  const [issueLines, setIssueLines] = useState<IssueLineForm[]>([emptyIssueLine()]);
  const [outputLines, setOutputLines] = useState<OutputLineForm[]>([emptyOutputLine()]);

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [endgoods, setEndgoods] = useState<EndgoodIdNameCategoryDto[]>([]);
  const [endgoodOptions, setEndgoodOptions] = useState<Endgood[]>([]);
  const [rawItems, setRawItems] = useState<RawItem[]>([]);
  const [processes, setProcesses] = useState<JobWorkProcessLookupDto[]>([]);
  const [wipWorkOrders, setWipWorkOrders] = useState<JobWorkWipWorkOrderDto[]>([]);
  const [savedWorkOrders, setSavedWorkOrders] = useState<JobWorkOrderWorkOrderDto[]>([]);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [wipLoading, setWipLoading] = useState(false);
  const [mastersLoading, setMastersLoading] = useState(true);
  const [wipSearch, setWipSearch] = useState('');

  const branchId = selectedBranch ?? null;

  const toBranchOptions = useMemo(
    () =>
      branches.filter(
        (b) => b.id !== branchId && b.isActive !== false
      ),
    [branches, branchId]
  );

  const loadMasters = useCallback(async () => {
    try {
      setMastersLoading(true);
      const [supRes, egCatRes, egIdNameRes, rawList, branchList] = await Promise.all([
        supplierApiService.getSuppliers(1, 200).catch(() => ({ success: false, data: [] as Supplier[] })),
        endgoodApiService.getEndgoodsIdNameCategory().catch(() => ({
          success: false,
          data: [] as EndgoodIdNameCategoryDto[],
        })),
        endgoodApiService.getEndgoodsIdName().catch(() => ({ success: false, data: [] as Endgood[] })),
        rawItemApiService.getRawItems().catch(() => [] as RawItem[]),
        branchApiService.getBranches().catch(() => [] as Branch[]),
      ]);

      setSuppliers(supRes.success && Array.isArray(supRes.data) ? supRes.data : []);
      setEndgoods(egCatRes.success && Array.isArray(egCatRes.data) ? egCatRes.data : []);
      setEndgoodOptions(egIdNameRes.success && Array.isArray(egIdNameRes.data) ? egIdNameRes.data : []);
      setRawItems(Array.isArray(rawList) ? rawList : []);
      setBranches(Array.isArray(branchList) ? branchList : []);
    } catch {
      toast.error('Failed to load master data');
    } finally {
      setMastersLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMasters();
  }, [loadMasters]);

  useEffect(() => {
    if (!endgoodId) {
      setProcesses([]);
      setWipWorkOrders([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        setProcessesLoading(true);
        const res = await jobWorkApiService.lookupProcessesForEndgood(parseInt(endgoodId, 10));
        if (cancelled) return;
        setProcesses(res.success && Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setProcesses([]);
      } finally {
        if (!cancelled) setProcessesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [endgoodId]);

  useEffect(() => {
    if (!endgoodId || !processId) {
      setWipWorkOrders([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        setWipLoading(true);
        const res = await jobWorkApiService.lookupWipWorkOrders(
          parseInt(endgoodId, 10),
          parseInt(processId, 10),
          branchId
        );
        if (cancelled) return;
        const fetched = res.success && Array.isArray(res.data) ? res.data : [];
        const fetchedIds = new Set(fetched.map((wo) => wo.workOrderId));
        const missingSaved = savedWorkOrders
          .filter((wo) => wo.workOrderId != null && !fetchedIds.has(wo.workOrderId))
          .map((wo) => ({
            workOrderId: wo.workOrderId,
            workOrderNumber: wo.workOrderNumber || `WO #${wo.workOrderId}`,
            endgoodId: Number(endgoodId),
            availableWipQty: wo.qty ?? wo.workOrderQty ?? 0,
            soNumber: '',
            fromProcessName: 'Already linked',
          }));
        setWipWorkOrders([...missingSaved, ...fetched]);
      } catch {
        if (!cancelled) setWipWorkOrders([]);
      } finally {
        if (!cancelled) setWipLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [endgoodId, processId, branchId, savedWorkOrders]);

  const filteredWipWorkOrders = useMemo(() => {
    const q = wipSearch.trim().toLowerCase();
    if (!q) return wipWorkOrders;
    return wipWorkOrders.filter(
      (wo) =>
        wo.workOrderNumber?.toLowerCase().includes(q) ||
        wo.soNumber?.toLowerCase().includes(q) ||
        wo.fromProcessName?.toLowerCase().includes(q)
    );
  }, [wipWorkOrders, wipSearch]);

  const selectedWorkOrderIds = useMemo(
    () => Object.keys(selectedWorkOrders).map((id) => Number(id)),
    [selectedWorkOrders]
  );

  const totalSelectedWoQty = useMemo(() => {
    let sum = 0;
    selectedWorkOrderIds.forEach((woId) => {
      const n = Number(selectedWorkOrders[woId]);
      if (!Number.isNaN(n) && n > 0) sum += n;
    });
    return sum;
  }, [selectedWorkOrderIds, selectedWorkOrders]);

  /** Processes at/after the selected start process — candidates for end process. */
  const endProcessOptions = useMemo(() => {
    if (!processId) return processes;
    const start = processes.find((p) => String(p.processId) === processId);
    if (start?.sequenceOrder == null) return processes;
    return processes.filter(
      (p) => p.sequenceOrder == null || p.sequenceOrder >= (start.sequenceOrder as number)
    );
  }, [processes, processId]);

  const toggleWorkOrder = (wo: JobWorkWipWorkOrderDto) => {
    setSelectedWorkOrders((prev) => {
      if (prev[wo.workOrderId] != null) {
        const next = { ...prev };
        delete next[wo.workOrderId];
        return next;
      }
      const defaultQty =
        wo.availableWipQty != null && !Number.isNaN(Number(wo.availableWipQty))
          ? String(wo.availableWipQty)
          : '';
      return { ...prev, [wo.workOrderId]: defaultQty };
    });
  };

  const setWorkOrderQty = (woId: number, qty: string) => {
    setSelectedWorkOrders((prev) => {
      if (prev[woId] == null) return prev;
      return { ...prev, [woId]: qty };
    });
  };

  // Keep WIP issue lines + output lines qty aligned with sum of selected WO qtys
  useEffect(() => {
    if (!endgoodId) return;
    const qtyStr = totalSelectedWoQty > 0 ? String(totalSelectedWoQty) : '';
    setIssueLines((prev) => {
      if (!prev.length) {
        return [
          {
            ...emptyIssueLine(),
            jobType: 'WIP',
            itemId: endgoodId,
            plannedQty: qtyStr,
          },
        ];
      }
      return prev.map((line) =>
        line.jobType === 'WIP'
          ? { ...line, itemId: endgoodId, plannedQty: qtyStr || line.plannedQty }
          : line
      );
    });
    setOutputLines((prev) => {
      if (!prev.length) return prev;
      return prev.map((line) => ({
        ...line,
        plannedQty: qtyStr || line.plannedQty,
      }));
    });
  }, [endgoodId, totalSelectedWoQty]);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    void (async () => {
      try {
        setLoading(true);
        const res = await jobWorkApiService.getOrderById(id);
        if (cancelled) return;
        if (!res.success || !res.data) {
          toast.error('Job work order not found');
          navigate('/jobwork/orders');
          return;
        }
        const order = res.data;
        if (order.status && order.status !== 'DRAFT') {
          toast.error('Only DRAFT orders can be edited');
          navigate(`/jobwork/orders/${id}/view`);
          return;
        }
        setSavedWorkOrders(order.workOrders ?? []);
        setPartyType(order.partyType);
        setSupplierId(order.supplierId != null ? String(order.supplierId) : '');
        setToBranchId(order.toBranchId != null ? String(order.toBranchId) : '');
        setEndgoodId(order.endgoodId != null ? String(order.endgoodId) : '');
        setProcessId(order.processId != null ? String(order.processId) : '');
        const woQtyMap: Record<number, string> = {};
        (order.workOrders ?? []).forEach((link) => {
          if (link.workOrderId != null) {
            woQtyMap[link.workOrderId] = String(link.qty ?? '');
          }
        });
        setSelectedWorkOrders(woQtyMap);
        setOrderDate(order.orderDate ? String(order.orderDate).slice(0, 10) : todayInputValue());
        setExpectedReturnDate(
          order.expectedReturnDate ? String(order.expectedReturnDate).slice(0, 10) : ''
        );
        setNotes(order.notes ?? '');
        setIssueLines(
          (order.issueLines?.length ? order.issueLines : []).map((line) => ({
            key: newKey(),
            jobType: line.jobType,
            itemId: String(
              jobWorkUsesRawItem(line.jobType) ? line.rawItemId ?? '' : line.endgoodId ?? ''
            ),
            plannedQty: String(line.plannedQty ?? ''),
            uom: line.uom ?? '',
            notes: line.notes ?? '',
          }))
        );
        if (!order.issueLines?.length) setIssueLines([emptyIssueLine()]);
        setOutputLines(
          (order.outputLines?.length ? order.outputLines : []).map((line) => {
            const jt = (line.jobType ?? 'FINISHED_GOOD') as JobWorkJobType;
            return {
              key: newKey(),
              jobType: jt,
              processId: line.processId != null ? String(line.processId) : '',
              plannedQty: String(line.plannedQty ?? ''),
              uom: line.uom ?? '',
              notes: line.notes ?? '',
            };
          })
        );
        if (!order.outputLines?.length) setOutputLines([emptyOutputLine()]);
      } catch {
        if (!cancelled) navigate('/jobwork/orders');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  const buildIssuePayload = (lines: IssueLineForm[]): JobWorkOrderIssueLineDto[] | null => {
    if (!endgoodId) {
      toast.error('Select an endgood');
      return null;
    }
    const endgoodIdNum = parseInt(endgoodId, 10);
    const out: JobWorkOrderIssueLineDto[] = [];
    for (const line of lines) {
      const isWip = line.jobType === 'WIP';
      const plannedQtyStr =
        isWip && totalSelectedWoQty > 0 ? String(totalSelectedWoQty) : line.plannedQty;
      const qty = Number(plannedQtyStr);
      if (!line.jobType || Number.isNaN(qty) || qty <= 0) {
        toast.error('Each issue line needs job type and planned qty > 0');
        return null;
      }
      if (jobWorkUsesRawItem(line.jobType)) {
        toast.error('Issue lines use the endgood selected above (WIP / finished good)');
        return null;
      }
      out.push({
        jobType: line.jobType,
        rawItemId: null,
        endgoodId: endgoodIdNum,
        plannedQty: qty,
        uom: line.uom.trim() || null,
        heatNo: null,
        notes: line.notes.trim() || null,
      });
    }
    if (out.length === 0) {
      toast.error('Add at least one issue line');
      return null;
    }
    return out;
  };

  const buildOutputPayload = (lines: OutputLineForm[]): JobWorkOrderOutputLineDto[] | null => {
    const filled = lines.filter((l) => l.processId || l.plannedQty || l.notes);
    if (filled.length === 0) return [];
    if (!endgoodId) {
      toast.error('Select an endgood');
      return null;
    }
    const endgoodIdNum = parseInt(endgoodId, 10);
    const qtyFromWos = totalSelectedWoQty > 0 ? totalSelectedWoQty : null;
    const out: JobWorkOrderOutputLineDto[] = [];
    for (const line of filled) {
      const qty = qtyFromWos ?? Number(line.plannedQty);
      if (!line.jobType || !line.processId || Number.isNaN(qty) || qty <= 0) {
        toast.error('Each output line needs job type, end process, and planned qty > 0');
        return null;
      }
      if (jobWorkUsesRawItem(line.jobType)) {
        toast.error('Output lines use the endgood selected above');
        return null;
      }
      out.push({
        jobType: line.jobType,
        rawItemId: null,
        endgoodId: endgoodIdNum,
        processId: parseInt(line.processId, 10),
        plannedQty: qty,
        uom: line.uom.trim() || null,
        conversionRate: null,
        notes: line.notes.trim() || null,
      });
    }
    return out;
  };

  const handleSave = async () => {
    if (branchId == null) {
      toast.error('Select a branch from the header');
      return;
    }
    if (partyType === 'VENDOR' && !supplierId) {
      toast.error('Select a supplier for vendor job work');
      return;
    }
    if (partyType === 'BRANCH') {
      if (!toBranchId) {
        toast.error('Select destination branch');
        return;
      }
      if (parseInt(toBranchId, 10) === branchId) {
        toast.error('Destination branch must be different from current branch');
        return;
      }
    }
    if (!endgoodId) {
      toast.error('Select an endgood');
      return;
    }
    if (!processId) {
      toast.error('Select a process');
      return;
    }
    if (!orderDate) {
      toast.error('Order date is required');
      return;
    }

    const workOrderLinks: JobWorkOrderWorkOrderDto[] = [];
    for (const woId of selectedWorkOrderIds) {
      const qtyRaw = selectedWorkOrders[woId];
      const qty = Number(qtyRaw);
      if (!qtyRaw?.trim() || Number.isNaN(qty) || qty <= 0) {
        toast.error('Enter a qty > 0 for each selected work order');
        return;
      }
      const wipRow = wipWorkOrders.find((w) => w.workOrderId === woId);
      const maxQty = wipRow?.availableWipQty != null ? Number(wipRow.availableWipQty) : null;
      if (maxQty != null && !Number.isNaN(maxQty) && qty > maxQty) {
        toast.error(
          `Qty for ${wipRow?.workOrderNumber ?? `WO #${woId}`} cannot exceed WIP qty (${maxQty})`
        );
        return;
      }
      workOrderLinks.push({ workOrderId: woId, qty });
    }

    const issuePayload = buildIssuePayload(issueLines);
    if (!issuePayload) return;
    const outputPayload = buildOutputPayload(outputLines);
    if (outputPayload == null) return;

    const endgoodIdNum = parseInt(endgoodId, 10);
    const processIdNum = parseInt(processId, 10);
    const payload: JobWorkOrderDto = {
      branchId,
      partyType,
      supplierId: partyType === 'VENDOR' ? parseInt(supplierId, 10) : null,
      toBranchId: partyType === 'BRANCH' ? parseInt(toBranchId, 10) : null,
      endgoodId: endgoodIdNum,
      processId: processIdNum,
      workOrders: workOrderLinks.length ? workOrderLinks : null,
      orderDate,
      expectedReturnDate: expectedReturnDate || null,
      notes: notes.trim() || null,
      createdBy: user?.id,
      updatedBy: user?.id,
      issueLines: issuePayload,
      outputLines: outputPayload,
    };

    try {
      setSaving(true);
      if (isEdit && id) {
        const res = await jobWorkApiService.updateOrder(id, payload);
        if (res.success && res.data?.id) {
          navigate(`/jobwork/orders/${res.data.id}/view`);
        }
      } else {
        const res = await jobWorkApiService.createOrder(payload);
        if (res.success && res.data?.id) {
          navigate(`/jobwork/orders/${res.data.id}/view`);
        }
      }
    } catch {
      // toast already shown
    } finally {
      setSaving(false);
    }
  };

  if (loading || mastersLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-4">
          <Button variant="outline" onClick={() => navigate('/jobwork/orders')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isEdit ? 'Update Draft' : 'Create Draft'}
          </Button>
        </div>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b">
            <CardTitle>{isEdit ? 'Edit Job Work Order' : 'New Job Work Order'}</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Party type</Label>
                <Select
                  value={partyType}
                  onValueChange={(v) => setPartyType(v as JobWorkPartyType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {JOB_WORK_PARTY_TYPES.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {partyType === 'VENDOR' ? (
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <Select value={supplierId || undefined} onValueChange={setSupplierId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContentWithSearch searchPlaceholder="Search supplier…" debounceMs={200}>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.supplierName}
                        </SelectItem>
                      ))}
                    </SelectContentWithSearch>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>To branch</Label>
                  <Select value={toBranchId || undefined} onValueChange={setToBranchId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContentWithSearch>
                      {toBranchOptions.map((b) => (
                        <SelectItem key={b.id} value={String(b.id)}>
                          {b.branchCode || b.branchName || `Branch ${b.id}`}
                        </SelectItem>
                      ))}
                    </SelectContentWithSearch>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Endgood</Label>
                <Select
                  value={endgoodId || undefined}
                  onValueChange={(v) => {
                    setEndgoodId(v);
                    setProcessId('');
                    setSelectedWorkOrders({});
                    setWipWorkOrders([]);
                    setWipSearch('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select endgood" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search endgood…" debounceMs={200}>
                    {endgoodOptions.map((eg) => (
                      <SelectItem key={eg.id} value={String(eg.id)}>
                        {eg.endgoodName}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Process (start)</Label>
                <Select
                  value={processId || undefined}
                  onValueChange={(v) => {
                    setProcessId(v);
                    setSelectedWorkOrders({});
                    setWipSearch('');
                    setOutputLines((prev) =>
                      prev.map((l) => ({ ...l, processId: '' }))
                    );
                  }}
                  disabled={!endgoodId || processesLoading}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        !endgoodId
                          ? 'Select endgood first'
                          : processesLoading
                            ? 'Loading processes…'
                            : 'Select process'
                      }
                    />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search process…" debounceMs={200}>
                    {processes.map((p) => (
                      <SelectItem key={p.processId} value={String(p.processId)}>
                        {p.sequenceOrder != null ? `${p.sequenceOrder}. ` : ''}
                        {p.processName}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>

              <div className="space-y-2 md:col-span-2 lg:col-span-3">
                <div className="flex items-center justify-between gap-2">
                  <Label>Work orders (WIP)</Label>
                  <span className="text-xs text-muted-foreground">
                    {selectedWorkOrderIds.length} selected
                  </span>
                </div>
                {!endgoodId || !processId ? (
                  <p className="text-sm text-muted-foreground rounded-md border border-dashed p-3">
                    Select endgood and process to load WIP work orders.
                  </p>
                ) : wipLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground p-3">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading WIP work orders…
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      placeholder="Search WO / SO / from process…"
                      value={wipSearch}
                      onChange={(e) => setWipSearch(e.target.value)}
                    />
                    <div className="max-h-56 overflow-y-auto rounded-md border">
                      {filteredWipWorkOrders.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-3 text-center">
                          No WIP work orders found.
                        </p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10" />
                              <TableHead>WO</TableHead>
                              <TableHead>From process</TableHead>
                              <TableHead className="text-right">WIP qty</TableHead>
                              <TableHead className="w-32 text-right">Qty</TableHead>
                              <TableHead>SO</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredWipWorkOrders.map((wo) => {
                              const checked = selectedWorkOrders[wo.workOrderId] != null;
                              const qtyValue = selectedWorkOrders[wo.workOrderId] ?? '';
                              return (
                                <TableRow
                                  key={wo.workOrderId}
                                  className="cursor-pointer"
                                  onClick={() => toggleWorkOrder(wo)}
                                >
                                  <TableCell onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                      checked={checked}
                                      onCheckedChange={() => toggleWorkOrder(wo)}
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium">{wo.workOrderNumber}</TableCell>
                                  <TableCell>{wo.fromProcessName || '—'}</TableCell>
                                  <TableCell className="text-right">
                                    {wo.availableWipQty != null ? wo.availableWipQty : '—'}
                                  </TableCell>
                                  <TableCell
                                    className="text-right"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Input
                                      type="number"
                                      min={0}
                                      step="any"
                                      disabled={!checked}
                                      value={qtyValue}
                                      placeholder={
                                        wo.availableWipQty != null
                                          ? String(wo.availableWipQty)
                                          : 'Qty'
                                      }
                                      className="h-8 text-right"
                                      onChange={(e) =>
                                        setWorkOrderQty(wo.workOrderId, e.target.value)
                                      }
                                    />
                                  </TableCell>
                                  <TableCell>{wo.soNumber || '—'}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="orderDate">Order date</Label>
                <Input
                  id="orderDate"
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expectedReturnDate">Expected return</Label>
                <Input
                  id="expectedReturnDate"
                  type="date"
                  max=""
                  value={expectedReturnDate}
                  onChange={(e) => setExpectedReturnDate(e.target.value)}
                />
              </div>

              <div className="space-y-2 md:col-span-2 lg:col-span-3">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Optional notes"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Issue lines</CardTitle>
              <p className="text-xs text-muted-foreground mt-1 font-normal">
                Uses endgood from above; planned qty is the sum of selected WO qtys.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIssueLines((p) => [...p, emptyIssueLine()])}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add line
            </Button>
          </CardHeader>
          <CardContent className="p-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job type</TableHead>
                  <TableHead>Planned qty</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {issueLines.map((line, idx) => {
                  const isWip = line.jobType === 'WIP';
                  const displayQty =
                    isWip && totalSelectedWoQty > 0
                      ? String(totalSelectedWoQty)
                      : line.plannedQty;
                  return (
                    <TableRow key={line.key}>
                      <TableCell className="min-w-[140px]">
                        <Select
                          value={line.jobType}
                          onValueChange={(v) => {
                            const jt = v as JobWorkJobType;
                            setIssueLines((prev) =>
                              prev.map((l, i) =>
                                i === idx
                                  ? {
                                      ...l,
                                      jobType: jt,
                                      itemId: endgoodId || '',
                                      plannedQty:
                                        jt === 'WIP' && totalSelectedWoQty > 0
                                          ? String(totalSelectedWoQty)
                                          : l.plannedQty,
                                    }
                                  : l
                              )
                            );
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {JOB_WORK_JOB_TYPES.filter((t) => !jobWorkUsesRawItem(t)).map((t) => (
                              <SelectItem key={t} value={t}>
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[110px]">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={displayQty}
                          disabled={isWip}
                          readOnly={isWip}
                          title={
                            isWip
                              ? 'Sum of selected work order quantities'
                              : undefined
                          }
                          onChange={(e) =>
                            setIssueLines((prev) =>
                              prev.map((l, i) =>
                                i === idx ? { ...l, plannedQty: e.target.value } : l
                              )
                            )
                          }
                        />
                      </TableCell>
                      <TableCell className="min-w-[90px]">
                        <Input
                          value={line.uom}
                          onChange={(e) =>
                            setIssueLines((prev) =>
                              prev.map((l, i) =>
                                i === idx ? { ...l, uom: e.target.value } : l
                              )
                            )
                          }
                        />
                      </TableCell>
                      <TableCell className="min-w-[140px]">
                        <Input
                          value={line.notes}
                          onChange={(e) =>
                            setIssueLines((prev) =>
                              prev.map((l, i) =>
                                i === idx ? { ...l, notes: e.target.value } : l
                              )
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={issueLines.length <= 1}
                          onClick={() =>
                            setIssueLines((prev) => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Output lines (optional)</CardTitle>
              <p className="text-xs text-muted-foreground mt-1 font-normal">
                Uses endgood from above; select end process (work done till). Qty matches WO sum.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setOutputLines((p) => [
                  ...p,
                  {
                    ...emptyOutputLine(),
                    plannedQty:
                      totalSelectedWoQty > 0 ? String(totalSelectedWoQty) : '',
                  },
                ])
              }
            >
              <Plus className="h-4 w-4 mr-1" />
              Add line
            </Button>
          </CardHeader>
          <CardContent className="p-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job type</TableHead>
                  <TableHead>End process</TableHead>
                  <TableHead>Planned qty</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {outputLines.map((line, idx) => {
                  const displayQty =
                    totalSelectedWoQty > 0
                      ? String(totalSelectedWoQty)
                      : line.plannedQty;
                  return (
                    <TableRow key={line.key}>
                      <TableCell className="min-w-[140px]">
                        <Select
                          value={line.jobType}
                          onValueChange={(v) =>
                            setOutputLines((prev) =>
                              prev.map((l, i) =>
                                i === idx
                                  ? { ...l, jobType: v as JobWorkJobType }
                                  : l
                              )
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {JOB_WORK_JOB_TYPES.filter((t) => !jobWorkUsesRawItem(t)).map((t) => (
                              <SelectItem key={t} value={t}>
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[220px]">
                        <Select
                          value={line.processId || undefined}
                          onValueChange={(v) =>
                            setOutputLines((prev) =>
                              prev.map((l, i) =>
                                i === idx ? { ...l, processId: v } : l
                              )
                            )
                          }
                          disabled={!endgoodId || !processId}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                !endgoodId || !processId
                                  ? 'Select endgood & start process first'
                                  : 'Select end process'
                              }
                            />
                          </SelectTrigger>
                          <SelectContentWithSearch
                            searchPlaceholder="Search process…"
                            debounceMs={200}
                          >
                            {endProcessOptions.map((p) => (
                              <SelectItem key={p.processId} value={String(p.processId)}>
                                {p.sequenceOrder != null ? `${p.sequenceOrder}. ` : ''}
                                {p.processName}
                              </SelectItem>
                            ))}
                          </SelectContentWithSearch>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[110px]">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={displayQty}
                          disabled
                          readOnly
                          title="Sum of selected work order quantities"
                        />
                      </TableCell>
                      <TableCell className="min-w-[90px]">
                        <Input
                          value={line.uom}
                          onChange={(e) =>
                            setOutputLines((prev) =>
                              prev.map((l, i) =>
                                i === idx ? { ...l, uom: e.target.value } : l
                              )
                            )
                          }
                        />
                      </TableCell>
                      <TableCell className="min-w-[140px]">
                        <Input
                          value={line.notes}
                          onChange={(e) =>
                            setOutputLines((prev) =>
                              prev.map((l, i) =>
                                i === idx ? { ...l, notes: e.target.value } : l
                              )
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setOutputLines((prev) => prev.filter((_, i) => i !== idx))
                          }
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-4">
          <Button
            onClick={() => void handleSave()}
            disabled={saving}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isEdit ? 'Update Draft' : 'Create Draft'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default JobWorkOrderFormPage;
